import { api } from '../client';
import type { Paged } from '../types';
import type { RelatedEntity } from '../types.crm';

export type NotificationType =
  | 'LEAD_ASSIGNED'
  | 'TASK_ASSIGNED'
  | 'TASK_DUE'
  | 'TASK_OVERDUE'
  | 'DEAL_WON'
  | 'DEAL_STAGE'
  | 'MENTION'
  | 'SYSTEM'
  | 'GENERAL'
  | 'LEAD_FOLLOW_UP'
  | 'EVENT_REMINDER';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  read: boolean;
  relatedType: RelatedEntity | null;
  relatedId: string | null;
  createdAt: string;
}

/** Email/SMS/WhatsApp/push are all preference-controlled (FR-33); in-app always delivers. */
export interface NotificationPrefs {
  emailEnabled: boolean;
  mutedEmailTypes: NotificationType[];
  smsEnabled: boolean;
  mutedSmsTypes: NotificationType[];
  whatsappEnabled: boolean;
  mutedWhatsappTypes: NotificationType[];
  pushEnabled: boolean;
  mutedPushTypes: NotificationType[];
}

export interface PushConfig {
  /** VAPID public key for PushManager.subscribe(); null when the server has no VAPID keypair configured. */
  publicKey: string | null;
  enabled: boolean;
}

export interface PushSubscriptionPayload {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  userAgent?: string;
}

export interface NotificationListQuery {
  page?: number;
  limit?: number;
  unread?: boolean;
  type?: NotificationType;
}

export const notificationService = {
  list: (q: NotificationListQuery = {}): Promise<Paged<Notification>> =>
    api.list<Notification>('/api/v1/notifications', q),
  unreadCount: (): Promise<{ count: number }> => api.get('/api/v1/notifications/unread-count'),
  markRead: (id: string): Promise<Notification> => api.post(`/api/v1/notifications/${id}/read`),
  markAllRead: (): Promise<{ updated: number }> => api.post('/api/v1/notifications/read-all'),
  remove: (id: string): Promise<{ id: string; deleted: boolean }> =>
    api.delete(`/api/v1/notifications/${id}`),
  getPreferences: (): Promise<NotificationPrefs> => api.get('/api/v1/notifications/preferences'),
  updatePreferences: (p: Partial<NotificationPrefs>): Promise<NotificationPrefs> =>
    api.patch('/api/v1/notifications/preferences', p),
  getPushConfig: (): Promise<PushConfig> => api.get('/api/v1/notifications/push/config'),
  subscribePush: (sub: PushSubscriptionPayload) => api.post('/api/v1/notifications/push-subscriptions', sub),
  unsubscribePush: (endpoint: string) =>
    api.delete('/api/v1/notifications/push-subscriptions', { data: { endpoint } }),
};
